// @flow

import Vue from 'vue'
import type { Commit, Dispatch } from 'vuex'
import type { SelectionState } from './state'
import type { ConfigurationCriteria, ConfigurationProperty } from '../../../products-api/types'
import type { Entity } from '../../../types/filter-entity/entity'
import type { SelectionFilterEntitySet } from '../../../types/selection-filter-entity-set'
import {
  DESELECT_FILTER_ENTITIES,
  SET_AVAILABLE_FILTER_ENTITIES,
  SET_PRODUCT_CONFIGURATIONS,
  SET_SELECTED_CUSTOMER_GROUPS, SET_SELECTED_MODALITIES, SET_SELECTED_PRODUCT_COLLECTIONS, SET_SELECTED_REGIONS
} from './mutation-types'
import { formatProductsApiDate } from '../../../utils/date'
import ProductConfiguration from '../../../types/product-configuration'
import type { CustomerGroup } from '../../../types/filter-entity/customer-group'
import type { ProductCollection } from '../../../types/filter-entity/product-collection'
import type { Modality } from '../../../types/filter-entity/modality'
import type { Region } from '../../../types/filter-entity/region'

type SelectionActionContext = { commit: Commit, dispatch: Dispatch, state: SelectionState, getters: Object, rootGetters: Object }

const buildCriteriaFromGetters = (getters: Object, rootGetters: Object): ConfigurationCriteria => {
  return {
    salesChannels: rootGetters['shop/salesChannelIds'],
    modalities: getters.availableSelectedModalities.map((entity: Entity) => entity.id),
    customerGroups: getters.availableSelectedCustomerGroups.map((entity: Entity) => entity.id),
    productCollections: getters.availableSelectedProductCollections.map((entity: Entity) => entity.id),
    regions: getters.availableSelectedRegions.map((entity: Entity) => entity.id)
  }
}

const fetchFilters = async ({commit, state, getters, rootGetters}: SelectionActionContext) => {
  // @todo: move to a more generic place?
  const criteria = buildCriteriaFromGetters(getters, rootGetters)
  const criteriaWithoutCustomerGroups = {...criteria, customerGroups: []}
  const criteriaWithoutProductCollections = {...criteria, productCollections: []}
  const criteriaWithoutModalities = {...criteria, modalities: []}

  const criteriaWithOnlyRegionsAndSalesChannels = {regions: criteria.regions, salesChannels: criteria.salesChannels}

  const variables = {
    termCriteria: criteriaWithOnlyRegionsAndSalesChannels,
    customerGroupCriteria: criteriaWithoutCustomerGroups,
    productCollectionCriteria: criteriaWithoutProductCollections,
    modalityCriteria: criteriaWithoutModalities,
    productCriteria: criteriaWithOnlyRegionsAndSalesChannels
  }
  try {
    const response = await Vue.prototype.$container.productsApiClient.getInitialFilters(variables)
    const newFilterEntities: SelectionFilterEntitySet = {
      ...state.availableFilterEntities,
      ...response.data.customerGroupFilters,
      ...response.data.productCollectionFilters,
      ...response.data.modalityFilters,
      ...response.data.productFilters,
      ...response.data.termFilters
    }
    commit(SET_AVAILABLE_FILTER_ENTITIES, newFilterEntities)
  } catch (e) {}
}

const fetchRegions = async ({commit, state, getters, rootGetters}: SelectionActionContext) => {
  const criteriaWithoutRegions = {
    ...buildCriteriaFromGetters(getters, rootGetters),
    modalities: [],
    customerGroups: [],
    productCollections: [],
    regions: []
  }
  const response = await Vue.prototype.$container.productsApiClient.getRegionFilters({
    criteria: criteriaWithoutRegions
  })
  const newFilterEntities: SelectionFilterEntitySet = {
    ...state.availableFilterEntities,
    ...response.data.filters
  }
  commit(SET_AVAILABLE_FILTER_ENTITIES, newFilterEntities)
}

const fetchProductConfigurations = async ({commit, state, getters, rootGetters}: SelectionActionContext) => {
  const futureCriteria: ConfigurationCriteria = {
    regions: getters.availableSelectedRegions.map(entity => entity.id),
    salesChannels: rootGetters['shop/salesChannelIds']
  }
  const currentCriteria: ConfigurationCriteria = {
    ...futureCriteria,
    startDate: formatProductsApiDate(new Date())
  }
  const bestOnePer: Array<ConfigurationProperty> = ['product', 'customerGroup', 'term']

  const productsApiClient = Vue.prototype.$container.productsApiClient

  const cheapestCurrentResponse = await productsApiClient.getBestProductConfigurations(currentCriteria, bestOnePer, 'cheapest')
  const earliestFutureResponse = await productsApiClient.getBestProductConfigurations(futureCriteria, bestOnePer, 'earliest')

  const keyFunction = (pc: ProductConfiguration) => pc.product.id + '|' + pc.customerGroup.id + '|' + pc.term.id

  const cheapestCurrentConfigurationsPerKey = group(
    keyFunction,
    cheapestCurrentResponse.data.configurations.filter(c => c !== null)
  )
  const earliestFutureConfigurations = earliestFutureResponse.data.configurations.filter(c => c !== null)

  const configurations = earliestFutureConfigurations.map(
    earliestFutureConfiguration => {
      const key = keyFunction(earliestFutureConfiguration)
      return (key in cheapestCurrentConfigurationsPerKey) ? cheapestCurrentConfigurationsPerKey[key][0] : earliestFutureConfiguration
    }
  )

  commit(SET_PRODUCT_CONFIGURATIONS, configurations)
}

async function setCustomerGroupSelection (
  {commit, dispatch}: SelectionActionContext,
  selectedCustomerGroups: Array<CustomerGroup>
): Promise<void> {
  // exclude unselectable entities from the selection:
  const selectedAndSelectableCustomerGroups = selectedCustomerGroups.filter(
    customerGroup => customerGroup.nrOfProductConfigurations > 0)
  commit(SET_SELECTED_CUSTOMER_GROUPS, selectedAndSelectableCustomerGroups)
  dispatch('fetchFilters')
}

async function setProductCollectionSelection (
  {commit, dispatch}: SelectionActionContext,
  selectedProductCollections: Array<ProductCollection>
) {
  // exclude unselectable entities from the selection:
  const selectedAndSelectableProductCollections = selectedProductCollections.filter(
    selected => selected.nrOfProductConfigurations > 0)
  commit(SET_SELECTED_PRODUCT_COLLECTIONS, selectedAndSelectableProductCollections)
  dispatch('fetchFilters')
}

async function setModalitySelection ({commit, dispatch}: SelectionActionContext, selectedModalities: Array<Modality>) {
  // exclude unselectable entities from the selection:
  const selectedAndSelectableModalities = selectedModalities.filter(
    selected => selected.nrOfProductConfigurations > 0)
  commit(SET_SELECTED_MODALITIES, selectedAndSelectableModalities)
  dispatch('fetchFilters')
}

async function setRegionSelection ({commit, dispatch}: SelectionActionContext, selectedRegions: Array<Region>) {
  // exclude unselectable entities from the selection:
  commit(SET_SELECTED_REGIONS, selectedRegions)
  dispatch('fetchFilters')
}

async function unselectFilterEntity ({commit, dispatch}: SelectionActionContext, entity: Entity) {
  commit(DESELECT_FILTER_ENTITIES, [entity])
  dispatch('fetchFilters')
}

async function unselectFilterEntities ({commit, dispatch}: SelectionActionContext, entities: Entity[]) {
  commit(DESELECT_FILTER_ENTITIES, entities)
  dispatch('fetchFilters')
}

type ArrayMap<T> = {[string]: Array<T>}

function group<T> (keyFunction: T => string, array: Array<T>): ArrayMap<T> {
  return array.reduce(
    (groups: ArrayMap<T>, item: T) => {
      const key = keyFunction(item)
      if (!(key in groups)) {
        groups[key] = []
      }
      groups[key].push(item)

      return groups
    },
    {}
  )
}

export default {
  fetchFilters,
  fetchRegions,
  fetchProductConfigurations,
  setCustomerGroupSelection,
  setProductCollectionSelection,
  setModalitySelection,
  setRegionSelection,
  unselectFilterEntity,
  unselectFilterEntities
}



// WEBPACK FOOTER //
// ./src/store/modules/selection/actions.js